import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Smartphone, ChevronLeft, ChevronRight } from "lucide-react";
import { useI18n } from "../i18n";
import { money } from "../lib/site";
import { useCart } from "../context/CartContext";

export default function SunsetSpecials({ items, bg, currency }) {
  const { lang, t } = useI18n();
  const navigate = useNavigate();
  const { add, setOpen } = useCart();
  const [i, setI] = useState(0);
  const list = items || [];
  const revealRef = useRef(null);
  const [shown, setShown] = useState(false);

  useEffect(() => {
    const el = revealRef.current;
    if (!el || !("IntersectionObserver" in window)) { setShown(true); return; }
    const obs = new IntersectionObserver((es) => es.forEach((e) => { if (e.isIntersecting) { setShown(true); obs.disconnect(); } }), { threshold: 0.15 });
    obs.observe(el);
    return () => obs.disconnect();
  }, [list.length]);

  useEffect(() => {
    if (list.length <= 1) return;
    const id = setInterval(() => setI((p) => (p + 1) % list.length), 5000);
    return () => clearInterval(id);
  }, [list.length]);

  if (!list.length) return null;
  const item = list[i % list.length];
  const name = lang === "es" ? item.name_es : item.name_en;
  const desc = lang === "es" ? item.desc_es : item.desc_en;
  const go = (d) => setI((p) => (p + d + list.length) % list.length);
  const startOrder = () => { add(item); setOpen(true); navigate("/menu"); };

  return (
    <section className="relative overflow-hidden" data-testid="sunset-specials">
      <img src={bg} alt="Atardecer en San Juan del Sur" className="absolute inset-0 w-full h-full object-cover" />
      <div className="absolute inset-0" style={{ background: "linear-gradient(90deg, rgba(10,25,47,0.94) 0%, rgba(10,25,47,0.74) 42%, rgba(224,122,40,0.30) 100%)" }} />
      <div ref={revealRef} className={`relative max-w-6xl mx-auto px-6 py-24 sm:py-28 text-center reveal ${shown ? "in" : ""}`}>
        <p className="text-xs font-semibold tracking-[0.25em] uppercase text-amber-400 mb-3" data-testid="sunset-eyebrow">{t.home.sunsetEyebrow}</p>
        <p className="font-sub text-slate-300 mb-8 max-w-xl mx-auto">{t.home.sunsetTag}</p>

        <div key={item.id} className="sunset-fade max-w-2xl mx-auto">
          <h2 onClick={startOrder} data-testid="sunset-name"
            className="font-script text-5xl sm:text-6xl lg:text-7xl text-white leading-[1.05] cursor-pointer hover:text-amber-300 transition-colors break-words">{name}</h2>
          {desc ? <p className="text-slate-200 mt-4 text-base sm:text-lg leading-relaxed">{desc}</p> : null}
          <div className="flex flex-wrap items-center justify-center gap-5 mt-7">
            <span className="font-display text-3xl sm:text-4xl text-amber-400" data-testid="sunset-price">{money(item.price, currency)}</span>
            <button onClick={startOrder} data-testid="sunset-order-btn"
              className="cta-glow inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold px-7 py-3 rounded-full transition-all hover:scale-[1.03]">
              <Smartphone className="h-4 w-4" /> {t.common.orderNow}
            </button>
          </div>
        </div>

        {list.length > 1 && (
          <div className="flex items-center justify-center gap-3 mt-10">
            <button onClick={() => go(-1)} data-testid="sunset-prev" aria-label="Previous special"
              className="h-10 w-10 rounded-full border border-white/20 text-white hover:bg-white/10 flex items-center justify-center transition-colors"><ChevronLeft className="h-5 w-5" /></button>
            <div className="flex gap-2">
              {list.map((_, k) => (
                <button key={k} onClick={() => setI(k)} data-testid={`sunset-dot-${k}`} aria-label={`Special ${k + 1}`}
                  className={`h-2.5 rounded-full transition-all ${k === (i % list.length) ? "w-8 bg-amber-400" : "w-2.5 bg-white/30 hover:bg-white/50"}`} />
              ))}
            </div>
            <button onClick={() => go(1)} data-testid="sunset-next" aria-label="Next special"
              className="h-10 w-10 rounded-full border border-white/20 text-white hover:bg-white/10 flex items-center justify-center transition-colors"><ChevronRight className="h-5 w-5" /></button>
          </div>
        )}
      </div>
    </section>
  );
}
